import { isArray, isObject } from 'lodash-es'
import { isPresent } from 'ts-extras'
import {
	Connection,
	CurrencyCode,
	ExtractConnections,
	Image,
	MediaContentType,
	MediaFragment,
	MediaFragment_MediaImage_,
	MediaImageFragment,
	MediaJson,
	NonNull,
	Nullable,
	Product,
	ProductJson,
	Variant
} from '~/types'
import { getCurrentCountry, getCurrentLanguage, includes } from '~/utils'
import { ASSET_URL, CDN_URL } from './constants'
import { toGraphId } from './storefront'

export interface ImageUrlOptions {
	width?: number
	height?: number
	crop?: 'top' | 'bottom' | 'center' | 'left' | 'right'
	format?: 'jpg' | 'pjpg'
	pad_color?: string
}

export type Dimension =
	| `${number}x`
	| `x${number}`
	| `${number}x${number}`
	| `${number}w`
	| `${number}h`
	| `${number}w${number}h`
export type ImageUrlDimensions = Dimension | number | ImageUrlOptions
export type ImageSrc = Image | MediaJson | string

/**
 * Convert to regular url to shopify CDN url with dimensions
 * @param url
 * @param dimensions
 */
export function imageUrl(src: ImageSrc, dimensions?: ImageUrlDimensions): string
export function imageUrl(src: Nullable<ImageSrc>, dimensions?: ImageUrlDimensions): string | undefined
export function imageUrl(src: Nullable<ImageSrc>, dimensions?: ImageUrlDimensions): string | undefined {
	if (!isPresent(src)) return undefined

	const url = typeof src === 'string' ? src.toString() : 'url' in src ? src.url : 'src' in src ? src.src : null
	if (!url) return undefined
	const ext = url.split('.').pop()

	switch (typeof dimensions) {
		case 'string':
			return url.replace(`.${ext}`, `_${dimensions}.${ext}`)
		case 'number':
			return url.replace(`.${ext}`, `_x${dimensions}.${ext}`)
		default: {
			const parsedUrl = new URL(url.startsWith('//') ? `https:${url}` : url)
			const baseWidth = window.innerWidth * window.devicePixelRatio
			const options = dimensions ?? { width: Math.ceil(baseWidth / 100) * 100 }
			Object.entries(options).forEach(([key, value]) =>
				parsedUrl.searchParams.set(key, typeof value === 'number' ? value.toFixed(0) : value)
			)
			return parsedUrl.toString()
		}
	}
}

export function filesUrl(filename: string) {
	return `${CDN_URL}/files/${filename}`
}

export function assetUrl(filename: string) {
	return `${ASSET_URL}/${filename}`
}

type ProductWithUrl = Pick<Product, 'handle' | 'onlineStoreUrl'>

export function productUrl<T extends Partial<ProductWithUrl>>(product: T | undefined): T['handle'] {
	if (!product || !product.handle) return undefined
	return `/products/${product.handle}`
}

/**
 * Check if user is on a shop page
 * @returns true if user is on a product(s) page
 */
export function isShop() {
	return includes(location.pathname, '/collections', '/products')
}

export function isProductPage() {
	return includes(location.pathname, '/products/')
}

export function hasVariants(product: Product) {
	return product.variants.length > 1
}

/** Return whether user is at landing page or not */
export function isHome() {
	return location.pathname == window.Shopify.routes.root
}

export function numberFormatter(options?: Intl.NumberFormatOptions) {
	const country = getCurrentCountry()
	const lang = getCurrentLanguage()
	return new Intl.NumberFormat(`${lang.locale}-${country.iso_code.toUpperCase()}`, options)
}

// eslint-disable-next-line @typescript-eslint/ban-types
export function isMediaImage(img: Nullable<MediaImageFragment> | {}): img is NonNull<MediaImageFragment> {
	return isPresent(img) && isImage((img as MediaImageFragment).image)
}

// eslint-disable-next-line @typescript-eslint/ban-types
export function isImage(img: unknown): img is Image {
	return isPresent(img) && isPresent((img as Image).url)
}

export function extractImages<T extends MediaFragment[]>(media: T): Image[] {
	const images = media
		?.filter((n): n is MediaFragment_MediaImage_ => n.mediaContentType === MediaContentType.IMAGE)
		.map(m => m.image as unknown)
		.filter(isPresent)

	return (images || []) as any
}

function extractNodes(data: Connection) {
	return data.edges.map(e => extractConnections(e.node))
}

export function extractConnections<T extends Record<string, unknown> | any[]>(data: T): ExtractConnections<T> {
	const isConn = (d: any): d is Connection => isArray(d?.edges)
	if (data == null) return null as any
	if (isConn(data)) return extractNodes(data) as ExtractConnections<T>
	if (isArray(data)) return data.filter(d => d).map(d => extractConnections(d)) as ExtractConnections<T>
	else {
		return Object.entries(data)
			.map(([key, value]) => [
				key,
				isConn(value)
					? extractNodes(value)
					: isObject(value) && !isArray(value)
					? extractConnections(value as any)
					: value
			])
			.reduce(
				(a, [key, value]) => ({
					...a,
					[key as string]: value
				}),
				{}
			) as ExtractConnections<T>
	}
}

type SimpleProductVariant = Omit<Product['variants'][0], 'product' | 'selectedOptions'>
export function toGqlProduct(product: ProductJson): Product {
	const toPriceV2 = (amount: number) => ({
		currencyCode: getCurrentCountry().currency.symbol as CurrencyCode,
		amount: `${amount / 100}`
	})
	return {
		id: toGraphId(product.id, 'product') as ProductID,
		title: product.title,
		subtitle: { value: product.subtitle || '' },
		onlineStoreUrl: productUrl(product),
		availableForSale: true,
		handle: product.handle,
		productType: product.type,
		variants: product.variants.map<SimpleProductVariant>(variant => ({
			...variant,
			id: toGraphId(variant.id, 'variant') as VariantID,
			availableForSale: variant.available,
			price: toPriceV2(variant.price),
			compareAtPrice: variant.compare_at_price ? toPriceV2(variant.compare_at_price) : null
		})) as Variant[],
		featuredImage: { url: product.featured_image },
		images: product.images.map(url => ({ url })),
		compareAtPriceRange: {
			maxVariantPrice: toPriceV2(product.compare_at_price_max),
			minVariantPrice: toPriceV2(product.compare_at_price_min)
		},
		priceRange: {
			maxVariantPrice: toPriceV2(product.price_max),
			minVariantPrice: toPriceV2(product.price_min)
		},
		options: product.options.map(opt => ({
			name: opt,
			optionValues: []
		})),
		descriptionHtml: product.description,
		upsell1: product.upsell1 ? { value: product.upsell1 } : null,
		upsell2: product.upsell2 ? { value: product.upsell2 } : null,
		upsell3: product.upsell3 ? { value: product.upsell3 } : null
	}
}

export interface UpsellInfo {
	id: VariantID
	label: string | undefined
	expiration: Date | null
	expirationText?: Nullable<string>
}

type ExtractUpsellsProduct = Pick<
	Product,
	| 'id'
	| 'upsell1'
	| 'upsell2'
	| 'upsell3'
	| 'upsell1_offer'
	| 'upsell2_offer'
	| 'upsell3_offer'
	| 'upsell1_expiry'
	| 'upsell2_expiry'
	| 'upsell3_expiry'
>

// TODO: Should result array contain null items?
export function extractUpsells(product: ExtractUpsellsProduct, overrides?: ExtractUpsellsProduct): UpsellInfo[] {
	return ([1, 2, 3] as const)
		.map(i => {
			const label = product[`upsell${i}_offer`]?.value
			const variantId = product[`upsell${i}`]?.reference?.variants?.nodes?.[0]?.id
			const date = product?.[`upsell${i}_expiry`]?.value
			const override = overrides?.[`upsell${i}`]?.value
			return {
				id: override || variantId,
				label,
				expiration: date ? new Date(date) : null
			}
		})
		.filter((upsell): upsell is UpsellInfo => upsell.id !== undefined)
}
